import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";

/**
 * Load-failure states. A skeleton must never spin forever — when a request
 * fails (or the app's network layer gives up) the skeleton is replaced by one
 * of these.
 *
 * SectionError — one card/region failed. Everything else on the page stays
 *                visible and interactive. This is the default.
 * PageError    — the screen cannot show any usable content at all.
 *
 * `onRetry` is optional on purpose. Omit it where retrying could do damage —
 * e.g. an order submission whose result is UNKNOWN, where resubmitting could
 * create a duplicate order. In that case the user gets the error and no retry.
 */

export const SectionError = ({
  message = "Data could not be loaded",
  onRetry,
  retryLabel = "Try Again",
  className = "",
}) => (
  <div
    role="alert"
    className={`flex flex-col items-start gap-2 py-5 ${className}`}
  >
    <p className="m-0 text-sm font-medium text-gray-900">{message}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="text-sm font-semibold text-primaryColor underline underline-offset-2"
      >
        {retryLabel}
      </button>
    )}
  </div>
);

export const PageError = ({
  title = "This page could not be loaded",
  body = "Check your connection and try again.",
  onRetry,
  retryLabel = "Try Again",
  className = "",
}) => (
  <div
    role="alert"
    className={`flex flex-col items-center justify-center text-center px-6 py-16 ${className}`}
  >
    <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F4F6]">
      <FontAwesomeIcon
        icon={faCircleExclamation}
        className="text-xl text-[#9CA3AF]"
      />
    </span>
    <p className="m-0 text-lg font-bold text-gray-900">{title}</p>
    <p className="mt-1 max-w-xs text-sm text-gray-500">{body}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 rounded-lg bg-primaryColor px-5 py-2.5 text-sm font-medium text-white"
      >
        {retryLabel}
      </button>
    )}
  </div>
);

export default SectionError;
